import { Modal } from "../../../shared/ui/Modal";
import { useMemo, useState } from "react";
import styles from "./AddStageModal.module.scss";
import { NormalButton } from "../../../shared/ui/Button";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { AxiosError } from "axios";
import { DropdownInput } from "@/frontend/shared/ui/DropdownInput";
import { Typography } from "@/frontend/shared/ui/Typography";
import { Input } from "@/frontend/shared/ui/Input";
import { createStage } from "../model/services/createStage";
import { CancelButton } from "@/frontend/shared/ui/Button/CancelButton/CancelButton";

type stageOptions = {
  label: string;
  value: string;
};

type TAddStageModalProps = {
  isOpen: boolean;
  onClose: () => void;
  triggerRefetch: () => void;
  stages: stageOptions[];
};

export function AddStageModal({ isOpen, onClose, triggerRefetch, stages }: TAddStageModalProps) {
  const [newStageName, setNewStageName] = useState<string>("");
  const [selectedDDItem, setSelectedDDItem] = useState<string>("");
  const [DDInputError, setDDInputError] = useState<string>("");
  const [newStageInputError, setNewStageInputError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  const dropdownStages: stageOptions[] = useMemo(() => {
    return stages.filter((stage) => stage.label !== "Archived" && stage.label !== "Completed");
  }, [stages]);

  function validateInputs() {
    let hasErrors = false;

    // Validate dropdown
    if (!selectedDDItem) {
      setDDInputError("Stage is not selected");
      hasErrors = true;
    } else if (!dropdownStages.map((stage) => stage.label).includes(selectedDDItem)) {
      setDDInputError("Stage doesn't exist");
      hasErrors = true;
    }

    const newStage = newStageName.trim();

    // Validate input
    if (stages.find((stage) => stage.label === newStage)) {
      setNewStageInputError("Stage with this name already exists");
      hasErrors = true;
    } else if (newStage.length === 0) {
      setNewStageInputError("Provide a name for new stage");
      hasErrors = true;
    } else if (newStage.length > 20) {
      setNewStageInputError("The name is too long");
      hasErrors = true;
    }

    return hasErrors;
  }

  function getIdByLabel(label: string) {
    const stage = dropdownStages.find((stage) => stage.label === label);
    return Number(stage?.value);
  }

  async function addStageHandler() {
    const errors = validateInputs();
    if (errors) return;

    try {
      setIsLoading(true);
      const newStage = newStageName.trim();
      await createStage({ name: newStage, prevStageId: getIdByLabel(selectedDDItem) });
      snackbarConstructor(`Stage ${newStage} was added successfully.`, "normal");
      onClose();
      triggerRefetch();
    } catch (error) {
      const axiosError = error as AxiosError<{ message: any }>;
      console.error(error);
      snackbarConstructor("Something went wrong", "error");
      reportErrorToSentry(axiosError?.response?.data.message ?? "Something went wrong", `/stages`);
    } finally {
      setIsLoading(false);
      setNewStageName("");
      setSelectedDDItem("");
      setDDInputError("");
      setNewStageInputError("");
    }
  }

  function onCloseModalHandler() {
    onClose();
    setNewStageName("");
    setSelectedDDItem("");
    setDDInputError("");
    setNewStageInputError("");
  }

  function onDDChangeHandler(value: string) {
    setDDInputError("");
    setSelectedDDItem(value);
  }

  function onDDSelectHandler(value: string) {
    setDDInputError("");
    setSelectedDDItem(value);
  }

  function onDDCloseHandler() {
    setIsDropdownOpen(false);
  }

  function onInputChangeHandler(value: string) {
    setNewStageName(value);
    setNewStageInputError("");
  }

  const isSubmitDisabled = isLoading || !newStageName || !selectedDDItem || !!DDInputError || !!newStageInputError;

  return (
    <div>
      <Modal isOpen={isOpen} onClose={onCloseModalHandler} title="Add Stage" isHeader>
        <div>
          <div className={newStageInputError ? styles.newStageContainerError : styles.newStageContainer}>
            <Typography className={styles.inputCaption} variant="label-4" color="greyscale700">
              Stage name
            </Typography>
            <Input
              value={newStageName}
              error={newStageInputError}
              isError={!!newStageInputError}
              onChange={(e) => onInputChangeHandler(e.target.value)}
              placeholder="Name"
              inputSize="small"
            />
          </div>
          <div className={DDInputError ? styles.placeAfterContainerError : styles.placeAfterContainer}>
            <Typography className={styles.inputCaption} variant="label-4" color="greyscale700">
              Place after
            </Typography>
            <DropdownInput
              value={selectedDDItem}
              onChange={(e) => onDDChangeHandler(e.target.value)}
              options={dropdownStages}
              onSelect={(value) => onDDSelectHandler(value)}
              placeholder="Select Stage"
              onClose={onDDCloseHandler}
              error={DDInputError}
              isOpen={isDropdownOpen}
              rightCaretIcon={true}
              onClick={() => {
                setIsDropdownOpen((prev) => !prev);
              }}
            />
          </div>
        </div>
        <div className={styles.optionContainer}>
          <CancelButton size="large" onClick={onCloseModalHandler}>
            Cancel
          </CancelButton>
          <NormalButton size="large" onClick={addStageHandler} isDisabled={isSubmitDisabled}>
            Add stage
          </NormalButton>
        </div>
      </Modal>
    </div>
  );
}
